import type {
  AcademicAnalyticsResult,
  GraduationCandidate,
  GraduationCandidateCounts,
} from '@dosfilos/application';

// ─── Panel académico: de la respuesta del servidor a lo que dibuja cada gráfico ─
// Puro y sin textos: la pantalla y los PDF de E dibujan desde aquí, y cada uno
// pone las palabras con i18n. La supresión ya viene del servidor (una tasa con
// n < mínimo llega sin numerador); aquí solo se respeta, nunca se recalcula.
// Los CONTEOS se muestran siempre (PM, 2026-10-27).

export type Pct =
  | { kind: 'value'; value: number; num: number; den: number }
  | { kind: 'suppressed'; den: number }
  | { kind: 'pending' };

type Rate = { num: number | null; den: number; value: number | null; suppressed: boolean };

export function pctOf(rate: Rate | null | undefined): Pct {
  if (!rate) return { kind: 'pending' };
  if (rate.suppressed || rate.value === null || rate.num === null) {
    return rate.den > 0 ? { kind: 'suppressed', den: rate.den } : { kind: 'pending' };
  }
  return { kind: 'value', value: rate.value, num: rate.num, den: rate.den };
}

// ── a) Activos y persistencia por período ──────────────────────────────────
export interface SeriesPoint {
  key: string;
  label: string;
  active: number;
  auditorsOnly: number;
  persistence: Pct;
}
export interface SeriesModel {
  /** Activos antes del inicio del análisis: cifra aparte, no un punto. */
  before: number | null;
  points: SeriesPoint[];
}

export function seriesModel(r: AcademicAnalyticsResult): SeriesModel {
  const before = r.series.find((p) => p.key === 'before');
  return {
    before: before ? before.active : null,
    points: r.series
      .filter((p) => p.key !== 'before')
      .map((p) => ({
        key: p.key,
        label: p.termName ?? p.key,
        active: p.active,
        auditorsOnly: p.auditorsOnly,
        persistence: pctOf(p.persistence),
      })),
  };
}

// ── b) Distribución de notas por curso ─────────────────────────────────────
/** Orden de los segmentos: el grupo que aprueba (con la D y «sin letra») y F-W. */
export const GRADE_SEGMENTS = ['A', 'B', 'C', 'D', 'other', 'F', 'W'] as const;
export type GradeSegment = (typeof GRADE_SEGMENTS)[number];
/** Los segmentos del grupo que aprueba. La D va ahí salvo que el seminario la
 *  sume a la tasa de alerta (D-F-W, el estándar); en LABTS es F-W. */
export function passSegments(dCountsInAlert: boolean): ReadonlySet<GradeSegment> {
  return new Set<GradeSegment>(
    dCountsInAlert ? ['A', 'B', 'C', 'other'] : ['A', 'B', 'C', 'D', 'other'],
  );
}

export interface DistributionRow {
  courseId: string;
  code: string;
  name: string;
  /** Inscripciones con nota final o retiro: la base del 100 %. */
  n: number;
  /** null si n < mínimo: la barra no se dibuja (se rotula «n < 10»). */
  segments: Array<{ key: GradeSegment; count: number; share: number }> | null;
  auditors: number;
  legacy: number;
  alert: Pct;
  /** ¿Sobre el umbral de alerta del seminario? */
  flag: boolean;
}

export function distributionModel(
  r: AcademicAnalyticsResult,
  courses: ReadonlyMap<string, { code: string; name: string }>,
): DistributionRow[] {
  return (
    r.courses
      .map((c) => {
        const info = courses.get(c.courseId);
        const counts = GRADE_SEGMENTS.map((key) => ({ key, count: c.distribution[key] ?? 0 }));
        const n = counts.reduce((a, s) => a + s.count, 0);
        const alert = pctOf(c.alertRate);
        return {
          courseId: c.courseId,
          code: info?.code ?? c.courseId,
          name: info?.name ?? '',
          n,
          segments:
            alert.kind === 'suppressed' || n === 0
              ? null
              : counts.map((s) => ({ ...s, share: s.count / n })),
          auditors: c.distribution.AUD,
          legacy: c.distribution.legacy,
          alert,
          flag: c.alertFlag,
        };
      })
      // Por código, NUNCA por tasa: no hay tablas de posiciones (PM).
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
  );
}

// ── c) Egreso acumulado por cohorte ────────────────────────────────────────
export interface CohortLine {
  key: string;
  fromYear: number | null;
  toYear: number | null;
  size: number;
  observedTerms: number;
  complete: boolean;
  /** null si la ventana está suprimida (n < mínimo): no se dibuja. */
  points: number[] | null;
  /** Posición fija de color: el color sigue a la ventana, no a su orden. */
  slot: number;
}
export interface CohortModel {
  normalTerms: number;
  horizon: number;
  withoutCourses: number;
  graduatedAfterHorizon: number;
  lines: CohortLine[];
}

export function cohortModel(r: AcademicAnalyticsResult, programId: string): CohortModel | null {
  const c = r.graduation.cohorts[programId];
  if (!c) return null;
  return {
    normalTerms: c.normalTerms,
    horizon: c.horizon,
    withoutCourses: c.withoutCourses,
    graduatedAfterHorizon: c.graduatedAfterHorizon,
    lines: c.windows.map((w, i) => {
      const suppressed = w.points.some((p) => p.suppressed) || w.size < r.params.minN;
      return {
        key: w.key,
        fromYear: w.fromYear,
        toYear: w.toYear,
        size: w.size,
        observedTerms: w.observedTerms,
        complete: w.complete,
        points: suppressed ? null : w.points.map((p) => p.value ?? 0),
        slot: i,
      };
    }),
  };
}

// ── d) Candidatos según cursos que faltan ──────────────────────────────────
/** Tramos de tiempo desde la última inscripción (los mismos del servidor). */
export const GAP_BUCKETS = ['now', '1-2', '3-4', '5+'] as const;
export type GapBucket = (typeof GAP_BUCKETS)[number];

export interface CandidatesModel {
  near: number;
  /** Fila k = les faltan k cursos (0 = cumplen). Solo conteos: no se suprime. */
  rows: Array<{ missing: number; total: number; byGap: Record<GapBucket, number> }>;
  total: number;
  /** Candidatos sin tramo de inscripción (no debería pasar): se informan, no se reparten. */
  withoutGap: number;
}

export function candidatesModel(
  candidates: readonly GraduationCandidate[],
  near: number,
  gapBuckets: AcademicAnalyticsResult['lastEnrollment']['buckets'],
): CandidatesModel {
  const gapOf = new Map<string, GapBucket>();
  for (const b of GAP_BUCKETS) for (const id of gapBuckets[b] ?? []) gapOf.set(id, b);
  // Una persona puede figurar en más de una credencial: cuenta una vez, con la
  // que tiene más cerca.
  const missingOf = new Map<string, number>();
  for (const c of candidates) {
    const m = c.eligible ? 0 : c.coursesMissing;
    if (m > near) continue;
    missingOf.set(c.personId, Math.min(missingOf.get(c.personId) ?? m, m));
  }
  const rows = Array.from({ length: near + 1 }, (_, missing) => ({
    missing,
    total: 0,
    byGap: { now: 0, '1-2': 0, '3-4': 0, '5+': 0 } as Record<GapBucket, number>,
  }));
  let withoutGap = 0;
  for (const [id, m] of missingOf) {
    const row = rows[m]!;
    row.total++;
    const gap = gapOf.get(id);
    if (gap) row.byGap[gap]++;
    else withoutGap++;
  }
  return { near, rows, total: missingOf.size, withoutGap };
}

/** El gráfico d desde las cifras del servidor (modo cifras), recortado al
 *  «cerca» elegido. Cada persona cuenta en su fila (su mínimo de cursos que
 *  faltan), así que recortar las filas es lo mismo que pedir de nuevo. */
export function countsModel(counts: GraduationCandidateCounts, near: number): CandidatesModel {
  const rows = counts.rows
    .filter((r) => r.missing <= near)
    .map((r) => ({ missing: r.missing, total: r.total, byGap: { ...r.byGap } }));
  const kept = counts.rows.filter((r) => r.missing <= near);
  return {
    near,
    rows,
    total: kept.reduce((a, r) => a + r.total, 0),
    withoutGap: kept.reduce((a, r) => a + r.withoutGap, 0),
  };
}

// ── Exportación (E) ─────────────────────────────────────────────────────────
/** Cursos por diapositiva de notas: con más, el texto no cabe en 16:9. */
export const COURSES_PER_SLIDE = 12;

/** Parte una lista en trozos de `n` (al menos uno, aunque esté vacía). */
export function chunk<T>(xs: readonly T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out.length ? out : [[]];
}
